import React, { useEffect, useMemo, useState } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { calculateRisk } from '../../utils/helpers';
import RiskCreateModal from './RiskCreateModal';
import RiskHeatmap from './RiskHeatmap';
import { MetricStrip } from '../common/MetricStrip';
import { riskAnalyticsServices } from '../../APIServices/riskAnalyticsServices';
import { usersManagementServices } from '../../APIServices/usersManagementServices';

function RiskModule({ onNavigate }) {
  const { state, setState, loadStudyList } = useData();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [studyFilter, setStudyFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [ownerList, setOwnerList] = useState([]);

  const syncRiskData = async () => {
    setLoading(true);
    try {
      const data = await riskAnalyticsServices.getRiskAnalyticsData('all', '', 1, 1000);
      const rows = (data?.data || []).map((row) => ({
        id: row.riskId,
        _id: row._id,
        riskId: row.riskId,
        code: row.code,
        studyId: row.studyId,
        title: row.title,
        p: row.p,
        i: row.i,
        c: row.c,
        owner: row.owner,
        status: row.status || 'open',
        dueAt: row.dueAt,
        description: row.description,
        mitigation: row.mitigation,
        createdAt: row.createdAt,
        updatedAt: row.updatedAt,
      }));

      setState((prev) => ({
        ...prev,
        risks: rows,
      }));
    } catch (error) {
      toast('Unable to load risk analytics data.', 'warn');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    syncRiskData();
  }, []);

  useEffect(() => {
    if (state.studies.length === 0) {
      loadStudyList?.();
    }
  }, [state.studies.length, loadStudyList]);

  const getOwnerValue = (user) => user?.user || user?.userId || user?.id || '';

  const getOwnerLabel = (user) => {
    const fullName = [user?.firstName, user?.middleName, user?.lastName].filter(Boolean).join(' ').trim();
    return fullName || user?.name || user?.email || 'Unassigned';
  };

  const loadOwners = async () => {
    try {
      let data = await usersManagementServices.getUserData('all', 'user', '', 1, 1000);
      let users = data?.data || [];
      setOwnerList(users);
    } catch (error) {
      setOwnerList([]);
      toast('Unable to load owner list.', 'warn');
    }
  };

  useEffect(() => {
    loadOwners();
  }, []);

  const filtered = useMemo(() => {
    return state.risks
      .filter((r) => studyFilter === 'all' || r.studyId === studyFilter)
      .map((r) => ({ ...r, ...calculateRisk(r.p, r.i, r.c) }))
      .sort((a, b) => b.score - a.score);
  }, [state.risks, studyFilter]);

  const getStudyCode = (studyId) => {
    const study = state.studies.find((s) => String(s.id || s.studyId) === String(studyId));
    return study?.code || study?.studyCode || '—';
  };

  const getOwnerName = (ownerId) => {
    const user = ownerList.find((u) => String(getOwnerValue(u)) === String(ownerId));
    return getOwnerLabel(user);
  };

  // Metrics calculations
  const openRisks = filtered.filter((r) => r.status === 'open' || r.status === 'mitigated');
  const criticalRisks = filtered.filter((r) => r.band === 'critical' && r.status !== 'closed');
  const overdue = filtered.filter((r) => r.dueAt && new Date(r.dueAt) < new Date() && r.status !== 'closed');
  const closedThisMonth = filtered.filter((r) => 
    r.status === 'closed' && 
    r.updatedAt && 
    new Date(r.updatedAt).getMonth() === new Date().getMonth()
  );

  const counts = {
    open: openRisks.length,
    critical: criticalRisks.length,
    overdue: overdue.length,
    closed: closedThisMonth.length
  };

  const handleRiskRowClick = () => {
    if (typeof onNavigate === 'function') {
      onNavigate('studies');
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Section 6 · Risk analytics</div>
          <h1 className="page-title">Risk Analytics</h1>
          <p className="page-sub">
            Configurable risk model: Probability × Impact × Control Weakness. Trends updated daily.
          </p>
        </div>
        <div className="flex gap-2" style={{ flexShrink: 0 }}>
          <select 
            className="btn" 
            value={studyFilter} 
            onChange={(e) => setStudyFilter(e.target.value)}
          >
            <option value="all">All studies</option>
            {state.studies.map((s) => (
              <option key={s.id || s.studyId} value={s.id || s.studyId}>{s.code || s.studyCode}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <i className="ti ti-plus" /> New risk
          </button>
        </div>
      </div>
      <MetricStrip
        items={[
          { label: 'Open risks', value: counts.open, foot: `across ${state.studies.length} studies` },
          { label: 'Critical risks', value: counts.critical, foot: `Down from ${counts.critical + 2}`, dir: counts.critical === 0 ? 'down' : 'up' },
          { label: 'Overdue mitigations', value: counts.overdue, foot: 'Escalation required' },
          { label: 'Closed this month', value: counts.closed, foot: '+50% vs last', dir: counts.closed > 0 ? 'down' : 'up' }
        ]}
      />

      {/* Heatmap */}
      <div className="section-label mt-4 mb-5">
        <i className="ti ti-chart-grid-dots" /> Risk heatmap — Probability × Impact
      </div>
      <RiskHeatmap risks={filtered.filter((r) => r.status !== 'closed')} />

      {/* Top risks table */}
      <div className="section-label mt-4 mb-5">
        <i className="ti ti-list" /> Top open risks
      </div>
      <div className="card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="buttonLoader" />
            <div style={{ marginTop: 12 }}>Loading risks...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty">
            <i className="ti ti-shield-off" />
            <div className="empty-title">No risks</div>
            <div className="tiny muted">Capture risks to enable portfolio analytics.</div>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Risk</th>
                <th>Study</th>
                <th>Score</th>
                <th>Owner</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 10).map((r) => {
                return (
                  <tr key={r.id} className="clickable-row" onClick={handleRiskRowClick}>
                    <td>
                      <div className="flex gap-2 center" style={{ flexWrap: 'wrap' }}>
                        <span className="id bold">{r.code}</span>
                        <span className="sm">· {r.title}</span>
                      </div>
                      <div className="xs mono muted">
                        {r.p} × {r.i} × {r.c} = {r.score}
                      </div>
                    </td>
                    <td className="id xs">{getStudyCode(r.studyId)}</td>
                    <td>
                      <span className={`tag tag-${r.variant === 'danger' ? 'danger' : r.variant === 'warn' ? 'warn' : r.variant === 'ok' ? 'ok' : 'info'}`}>
                        {r.score}
                      </span>
                    </td>
                    <td className="xs">{getOwnerName(r.owner)}</td>
                    <td>
                      <i className="ti ti-chevron-right clickable-row-arrow" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <RiskCreateModal 
        open={showCreate} 
        onClose={() => setShowCreate(false)}
        onCreated={syncRiskData}
      />

      <style>{`
        .clickable-row {
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .clickable-row:hover {
          background: var(--surface-2);
        }

        .clickable-row-arrow {
          color: var(--ink-3);
          font-size: 14px;
          opacity: 0.75;
        }

        .clickable-row:hover .clickable-row-arrow {
          opacity: 1;
          color: var(--ink);
        }
      `}</style>
    </div>
  );
}

export default RiskModule;